/**
 * Lift2Fit — coach dashboard.
 *
 * Everything a coach has, plus every client across every coach, the active
 * programme in a clean read-only list, and programme authoring: the coach
 * collects an intake and calls POST /programme/one-time.
 */
import { useState } from 'react';
import {
  api,
  type GeneratedPlan,
  type PlanIntake,
  type RosterDashboard as RosterDashboardPayload,
  type Trainee,
  type TrainingTrack,
} from '@/lib/api';
import { useSession } from '@/lib/useSession';
import { cn } from '@/lib/format';
import { Button } from '@/components/ui';
import { AlertIcon, ArrowRightIcon, SparkIcon } from '@/lib/icons';
import { EmptyState, LoadingPanel, Panel, PersonaBadge, ScreenHeader, StatusChip, TrackBadge, TrackIcon } from '../components';
import { ProgrammeDays } from '../programme';
import { InviteForm, NeedsAttention, OwnWeekPanel, RosterSummary, TraineeList } from '../roster';
import { GOAL_OPTIONS, TRACKS, defaultGoalForTrack, trackMeta } from '../demoData';
import { useAsync, saveGeneratedPlan } from '../hooks';
import { Link, navigate } from '../router';
import { useUnitPreference } from '@/lib/useUnitPreference';

const EXPERIENCE: Array<PlanIntake['experience']> = ['beginner', 'intermediate', 'advanced'];
const DAYS: Array<PlanIntake['daysPerWeek']> = [2, 3, 4, 5, 6];
const MINUTES: Array<PlanIntake['sessionMinutes']> = [30, 45, 60, 75, 90];

/* ---------------------------------------------------------------------------
   Programme authoring
   ------------------------------------------------------------------------ */

function CoachGeneratePanel({ trainees, onGenerated }: { trainees: Trainee[]; onGenerated: (plan: GeneratedPlan) => void }) {
  const session = useSession();
  const [traineeId, setTraineeId] = useState(trainees[0]?.userId ?? '');
  const [track, setTrack] = useState<TrainingTrack>((session.user?.track ?? 'general') as TrainingTrack);
  const [goal, setGoal] = useState<PlanIntake['goal']>(defaultGoalForTrack((session.user?.track ?? 'general') as TrainingTrack));
  const [experience, setExperience] = useState<PlanIntake['experience']>('intermediate');
  const [daysPerWeek, setDaysPerWeek] = useState<PlanIntake['daysPerWeek']>(4);
  const [sessionMinutes, setSessionMinutes] = useState<PlanIntake['sessionMinutes']>(60);
  const [injuryFlags, setInjuryFlags] = useState<string>('none');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const generate = async () => {
    setBusy(true);
    setMessage('Building the block…');
    const intake: PlanIntake = {
      goal,
      track,
      experience,
      daysPerWeek,
      sessionMinutes,
      heightCm: 178,
      bodyweightKg: 82.4,
      age: 34,
      sex: 'unspecified',
      equipment: ['barbell', 'dumbbell', 'machine', 'cable', 'bodyweight', 'kettlebell', 'band'],
      injuryFlags: injuryFlags === 'none' ? [] : injuryFlags.split(',').map((flag) => flag.trim()),
      notes: traineeId ? `Prescribed for ${trainees.find((t) => t.userId === traineeId)?.displayName ?? 'client'}` : undefined,
    };
    const result = await api.generatePlan(intake);
    saveGeneratedPlan(result.data);
    setBusy(false);
    setMessage(
      result.source === 'demo'
        ? 'Demo data — start the API to generate against the real engine. The plan below is a faithful sample.'
        : 'Plan generated and saved to the client’s account.',
    );
    onGenerated(result.data);
  };

  return (
    <Panel
      title="Author a programme"
      subtitle="Collect the intake, generate the block, then review it as day cards."
      as="section"
    >
      <div className="grid gap-4 md:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Client</span>
          <select
            value={traineeId}
            onChange={(event) => setTraineeId(event.target.value)}
            className="h-11 border border-hairline bg-page px-3 text-[14px] text-ink"
          >
            {trainees.map((trainee) => (
              <option key={trainee.userId} value={trainee.userId}>
                {trainee.displayName} · {trainee.trackLabel}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">What are we training for?</span>
          <select
            value={track}
            onChange={(event) => {
              const next = event.target.value as TrainingTrack;
              setTrack(next);
              setGoal(defaultGoalForTrack(next));
            }}
            className="h-11 border border-hairline bg-page px-3 text-[14px] text-ink"
          >
            {TRACKS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label} — {trackMeta(option.id).persona}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Goal</span>
          <select
            value={goal}
            onChange={(event) => setGoal(event.target.value as PlanIntake['goal'])}
            className="h-11 border border-hairline bg-page px-3 text-[14px] text-ink"
          >
            {GOAL_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Experience</span>
          <select
            value={experience}
            onChange={(event) => setExperience(event.target.value as PlanIntake['experience'])}
            className="h-11 border border-hairline bg-page px-3 text-[14px] text-ink"
          >
            {EXPERIENCE.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Days per week</span>
          <select
            value={daysPerWeek}
            onChange={(event) => setDaysPerWeek(Number(event.target.value) as PlanIntake['daysPerWeek'])}
            className="h-11 border border-hairline bg-page px-3 text-[14px] text-ink"
          >
            {DAYS.map((option) => (
              <option key={option} value={option}>
                {option} days
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">Session length</span>
          <select
            value={sessionMinutes}
            onChange={(event) => setSessionMinutes(Number(event.target.value) as PlanIntake['sessionMinutes'])}
            className="h-11 border border-hairline bg-page px-3 text-[14px] text-ink"
          >
            {MINUTES.map((option) => (
              <option key={option} value={option}>
                {option} minutes
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1.5 md:col-span-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink-muted">
            Injury flags (comma separated, or “none”)
          </span>
          <input
            type="text"
            value={injuryFlags}
            onChange={(event) => setInjuryFlags(event.target.value)}
            className="h-11 border border-hairline bg-page px-3 text-[14px] text-ink"
          />
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button size="lg" onClick={generate} disabled={busy}>
          {busy ? 'Generating…' : 'Generate plan'}
          {!busy ? <SparkIcon /> : null}
        </Button>
        <span className="text-[12px] text-ink-muted">
          Deterministic volume landmarks first, prose second. Every prescription carries its rationale.
        </span>
      </div>

      <p aria-live="polite" className="min-h-[20px] text-[13px] text-ink-muted">
        {message ?? ''}
      </p>
    </Panel>
  );
}

/* ---------------------------------------------------------------------------
   Screen
   ------------------------------------------------------------------------ */

export function CoachDashboard() {
  const session = useSession();
  const { unit } = useUnitPreference();
  const track = session.user?.track ?? 'general';
  const state = useAsync(() => api.dashboard('coach'), [track]);
  const programmeState = useAsync(() => api.programme(), [track]);
  const [invited, setInvited] = useState<Trainee[]>([]);
  const [plan, setPlan] = useState<GeneratedPlan | null>(null);

  const data =
    state.data && (state.data.role === 'coach' || state.data.role === 'trainer')
      ? (state.data as RosterDashboardPayload)
      : null;
  const meta = trackMeta(data?.track ?? track);
  const activePlan = plan ?? programmeState.data;

  if (state.loading && !data) {
    return (
      <div className="flex flex-col gap-6">
        <LoadingPanel label="Loading the coach view" rows={3} />
        <LoadingPanel label="Loading every client" rows={5} />
      </div>
    );
  }

  if (!data) {
    return (
      <EmptyState
        title="We could not read the coach dashboard"
        body={state.error ?? 'The API did not answer and there is no demo roster for this role.'}
        icon={<AlertIcon className="h-8 w-8 text-accent" />}
      />
    );
  }

  const trainees = [...invited, ...data.trainees.filter((trainee) => !invited.some((i) => i.userId === trainee.userId))];

  return (
    <div className="flex flex-col gap-6">
      <ScreenHeader
        eyebrow="Coach"
        title={`${data.greeting.displayName.split(' ')[0]}’s coaching room`}
        badge={<TrackBadge track={data.track} />}
        lead={`${meta.persona} view — every client across every coach, plus programme authoring.`}
        actions={
          <span className="flex flex-wrap items-center gap-2">
            <PersonaBadge track={data.track} persona={data.trackMetrics.coachPersona} />
            <Link
              to="/plan/new"
              className="inline-flex h-11 items-center gap-2 border border-hairline px-4 text-[12px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
            >
              Full plan wizard
              <ArrowRightIcon />
            </Link>
          </span>
        }
      />

      <RosterSummary dashboard={data} />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <OwnWeekPanel week={data.ownWeek} streak={data.ownStreak} sessionWord={meta.sessionWord} />
        <NeedsAttention attention={data.attention} />
      </div>

      <Panel
        title="Active programme"
        subtitle={
          activePlan
            ? `${activePlan.name} · ${activePlan.weeks} weeks · ${activePlan.daysPerWeek} days per week`
            : 'No programme generated for this track yet.'
        }
        actions={
          <span className="flex flex-wrap items-center gap-1.5">
            {activePlan ? <StatusChip tone="accent">{activePlan.trackLabel}</StatusChip> : null}
            {activePlan ? <StatusChip tone="neutral">Goal {activePlan.goal}</StatusChip> : null}
          </span>
        }
      >
        {programmeState.loading && !activePlan ? (
          <LoadingPanel label="Loading the active programme" rows={4} />
        ) : activePlan ? (
          <ProgrammeDays plan={activePlan} unit={unit} />
        ) : (
          <EmptyState
            title="No programme yet"
            body="Collect an intake below and generate the first block — strength, bodybuilding, conditioning, performance, wellness or general."
            icon={<SparkIcon className="h-8 w-8 text-accent" />}
          />
        )}
      </Panel>

      <CoachGeneratePanel
        trainees={trainees}
        onGenerated={(generated) => {
          setPlan(generated);
          navigate(`/plan/${generated.planId}`);
        }}
      />

      <TraineeList trainees={trainees} showTrainer title="Every client" />

      <InviteForm onInvited={(trainee) => setInvited((prev) => [trainee, ...prev])} />

      <p className={cn('text-[12px] text-ink-muted')}>
        Track focus: {meta.metricsFocus.join(' · ')}. Switching the demo track in the header re-labels this whole view.
      </p>
    </div>
  );
}
